/**
 * WHO wrote something off, and WHO approved it — recorded on every void and
 * comp, and on the removal of either.
 *
 * Owner, 2026-09-22: "void and comps need tracking of the user for void/comp
 * and also the manager user that approved the void/comp". Each record used to
 * carry ONE id, `authorizedBy`, and it meant different people on different
 * paths — the PIN'd manager on a comp, the signed-in employee on a no-approval
 * void — so nothing could say who pressed the button AND who signed it off.
 *
 * Flat keys, the SAME on every record that carries them (an item's
 * `voidInfo`, an applied comp, an order's `voidInfo`, a write-off history
 * entry), so one reader pulls them from any of them. The till's mirror is
 * `apps/pos/lib/models/order/write_off_actors.dart`.
 *
 *   - `authorizedBy` (on the record itself) — whose authority it ran on.
 *   - `performedBy` — the employee signed in on the till who did it. A CLAIM
 *     by the till, recorded as attribution; the permission gate verifies the
 *     approver, never this.
 *   - `approvedWithPin` — a manager typed their PIN for it (a step-up). False
 *     means the performer held the permission and nobody else was asked.
 *   - `*Name` — the names as the till knew them at the time, so a report reads
 *     after an employee is renamed or removed. The ids remain the identity.
 *   - `approvalRequestGuid` / `approvalChannel` — the approver answered a
 *     REMOTE approval request (`manager_approval_requests/{guid}`) from the
 *     manager app or a text link. The request document is the server-side
 *     witness; docs/superpowers/specs/2026-10-16-remote-manager-approval-design.md.
 */

export interface IWriteOffActors {
  readonly performedBy?: string;
  readonly performedByName?: string;
  readonly authorizedByName?: string;
  readonly approvedWithPin?: boolean;
  readonly approvalRequestGuid?: string;
  readonly approvalChannel?: 'app' | 'sms_link';
}

/** One void or comp that was TAKEN BACK on a line — append-only. */
export interface IWriteOffHistoryEntry extends IWriteOffActors {
  /** `void_removed` (the unvoid route) or `comp_removed` (the till's Remove Comp). */
  readonly type: 'void_removed' | 'comp_removed' | string;
  /** Who REMOVED it — the approver of the removal. */
  readonly authorizedBy?: string;
  readonly removedAt?: unknown;
  readonly reason?: string;
  /** Anything else the writer recorded (the original void's or comp's actors). */
  readonly [key: string]: unknown;
}

/** Longest name kept. A name is attribution text, never an unbounded blob. */
const MAX_NAME = 120;
/** Longest id kept — employee ids are short numeric strings. */
const MAX_ID = 64;

/**
 * The actor keys from an untrusted object (a request body, a Firestore map),
 * keeping only well-formed values. Absent stays absent — a missing performer
 * is recorded as missing, never filled in from somebody else.
 */
export function readWriteOffActors(raw: unknown): IWriteOffActors {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const str = (v: unknown, max: number): string | undefined =>
    typeof v === 'string' && v.trim().length > 0 ? v.trim().slice(0, max) : undefined;
  const out: {
    performedBy?: string;
    performedByName?: string;
    authorizedByName?: string;
    approvedWithPin?: boolean;
    approvalRequestGuid?: string;
    approvalChannel?: 'app' | 'sms_link';
  } = {};
  const performedBy = str(r.performedBy, MAX_ID);
  if (performedBy) out.performedBy = performedBy;
  const performedByName = str(r.performedByName, MAX_NAME);
  if (performedByName) out.performedByName = performedByName;
  const authorizedByName = str(r.authorizedByName, MAX_NAME);
  if (authorizedByName) out.authorizedByName = authorizedByName;
  if (typeof r.approvedWithPin === 'boolean') out.approvedWithPin = r.approvedWithPin;
  const approvalRequestGuid = str(r.approvalRequestGuid, MAX_ID);
  if (approvalRequestGuid) out.approvalRequestGuid = approvalRequestGuid;
  if (r.approvalChannel === 'app' || r.approvalChannel === 'sms_link') out.approvalChannel = r.approvalChannel;
  return out;
}

/** A write-off history list from Firestore: entries that are objects with a `type`. */
export function readWriteOffHistory(raw: unknown): IWriteOffHistoryEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is IWriteOffHistoryEntry =>
      !!e && typeof e === 'object' && typeof (e as { type?: unknown }).type === 'string',
  );
}
